import { useMemo } from 'react';
import {
  Button,
  CloseIcon,
  DefaultWarningButtonIcon,
  Dialog,
  InputText,
  InputTextarea,
  globalToastService,
} from '@repo/atomic-components';
import { Field, Form } from 'react-final-form';
import type { ToolkitTool } from '@/api/toolkit';
import { useCreateSuggestedToolMutation, useGetToolGroupCollectionsQuery } from '@/api/toolkit';
import { SearchableSelect } from '@/ui/components/SearchableSelect';
import { keepFocusOnPress } from '@/utils/keepFocusOnPress';

export interface RequestGlobalToolkitModalProps {
  visible: boolean;
  onHide: () => void;
  tool: ToolkitTool | null;
}

type FormValues = {
  collectionId: string;
  groupId: string;
  toolName: string;
  toolDescription: string;
  prompt: string;
  comments: string;
};

const validate = (values: FormValues) => {
  const errors: Partial<Record<keyof FormValues, string>> = {};
  if (!values.collectionId?.trim()) errors.collectionId = 'Tool collection is required';
  if (!values.groupId?.trim()) errors.groupId = 'Tool group is required';
  if (!values.toolName?.trim()) errors.toolName = 'Tool name is required';
  if (!values.toolDescription?.trim()) errors.toolDescription = 'Tool description is required';
  if (!values.prompt?.trim()) errors.prompt = 'Prompt is required';
  if (!values.comments?.trim()) errors.comments = 'Comments are required';

  return errors;
};

const INPUT_CLASS =
  '!text-theme-black !border-theme-border-grey !bg-theme-white h-8 w-full rounded-lg !text-sm placeholder:!text-sm';

export const RequestGlobalToolkitModal = ({ visible, onHide, tool }: RequestGlobalToolkitModalProps) => {
  const [createSuggestedTool, { isLoading }] = useCreateSuggestedToolMutation();
  const { data } = useGetToolGroupCollectionsQuery(undefined, { skip: !visible });

  const collectionOptions = useMemo(
    () => data?.collections.map((c) => ({ label: c.collectionName, value: c.collectionId })),
    [data],
  );

  if (!tool) {
    return null;
  }

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      modal
      draggable={false}
      closable={false}
      header={
        <div className="text-theme-black flex w-full items-start justify-between gap-4">
          <span className="text-[1.25rem] leading-7.5 font-semibold">
            Request for user prompt to be added to shared Toolkit
          </span>
          <Button
            type="button"
            onMouseDown={keepFocusOnPress}
            onClick={onHide}
            outlined
            className="text-theme-black hover:text-theme-black flex h-8 w-8 shrink-0 items-center justify-center rounded border-none transition-colors hover:bg-transparent"
            aria-label="Close"
          >
            <CloseIcon className="h-[14px] w-[14px]" />
          </Button>
        </div>
      }
      className="!w-[min(42.5rem,calc(100vw-48px))]"
      pt={{
        root: {
          className: 'rounded-lg dialog-shadow flex max-h-[calc(100vh-48px)] flex-col',
        },
        header: {
          className:
            'min-h-[4.625rem] cursor-default !rounded-t-lg bg-theme-white text-theme-black border-theme-border-grey shrink-0 border-b px-6 py-4',
        },
        headerTitle: { className: '!m-0 !w-full' },
        content: {
          className: 'flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto bg-theme-white px-6 pt-4 pb-6 rounded-b-lg',
        },
        closeButton: { className: 'hidden' },
      }}
    >
      <Form<FormValues>
        key={tool.toolId}
        onSubmit={async (values, apiForm) => {
          try {
            await createSuggestedTool({
              sourceToolId: tool.toolId,
              collectionId: values.collectionId,
              groupId: values.groupId,
              toolName: values.toolName.trim(),
              toolDescription: values.toolDescription.trim(),
              prompt: values.prompt.trim(),
              comments: values.comments.trim(),
            }).unwrap();
            globalToastService.show({
              closable: true,
              severity: 'success',
              summary: 'Your prompt request has been sent',
              life: 6000,
            });
            apiForm.reset();
            onHide();
          } catch {
            globalToastService.show({
              closable: true,
              severity: 'error',
              summary: 'Failed to submit, please try again later.',
              life: 6000,
            });
          }
        }}
        initialValues={{
          collectionId: '',
          groupId: '',
          toolName: tool.toolName,
          toolDescription: tool.description,
          prompt: tool.prompt,
          comments: '',
        }}
        validate={validate}
      >
        {({ handleSubmit, invalid, submitting, values, form }) => {
          const groupOptions =
            data?.collections
              .find((c) => c.collectionId === values?.collectionId)
              ?.groups.map((g) => ({ label: g.groupName, value: g.groupId })) ?? [];

          return (
            <form onSubmit={handleSubmit} className="flex flex-col gap-1">
              <Field<string> name="collectionId">
                {({ input, meta }) => (
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="toolkit-collection" className="text-theme-black block text-sm font-[600]">
                      Toolkit Collection
                    </label>
                    <SearchableSelect
                      inputId="toolkit-collection"
                      value={input.value || null}
                      options={collectionOptions ?? []}
                      placeholder="Search for Toolkit Collection"
                      onChange={(v) => {
                        input.onChange(v ?? '');
                        form.change('groupId', '');
                      }}
                      onBlur={input.onBlur}
                    />
                    <div className="min-h-4 text-xs text-red-600">{meta.touched && meta.error ? meta.error : ''}</div>
                  </div>
                )}
              </Field>

              <Field<string> name="groupId">
                {({ input, meta }) => (
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="toolkit-group" className="text-theme-black block text-sm font-[600]">
                      Toolkit Group
                    </label>
                    {/* Remount on collection change so the previous group's text doesn't linger in the input */}
                    <SearchableSelect
                      key={values?.collectionId || 'no-collection'}
                      inputId="toolkit-group"
                      value={input.value || null}
                      options={groupOptions}
                      placeholder="Search for Toolkit Group"
                      onChange={(v) => input.onChange(v ?? '')}
                      onBlur={input.onBlur}
                      disabled={!values?.collectionId}
                    />
                    <div className="min-h-4 text-xs text-red-600">{meta.touched && meta.error ? meta.error : ''}</div>
                  </div>
                )}
              </Field>

              <Field<string> name="toolName">
                {({ input, meta }) => (
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="toolkit-prompt-name" className="text-theme-black block text-sm font-[600]">
                      Prompt Name
                    </label>
                    <InputText
                      id="toolkit-prompt-name"
                      className={INPUT_CLASS}
                      placeholder="Prompt name"
                      value={input.value}
                      onChange={(e) => input.onChange(e.target.value)}
                      onBlur={input.onBlur}
                      onFocus={input.onFocus}
                    />
                    <div className="min-h-4 text-xs text-red-600">{meta.touched && meta.error ? meta.error : ''}</div>
                  </div>
                )}
              </Field>

              <Field<string> name="toolDescription">
                {({ input, meta }) => (
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="toolkit-prompt-description" className="text-theme-black block text-sm font-[600]">
                      Prompt Description
                    </label>
                    <InputText
                      id="toolkit-prompt-description"
                      className={INPUT_CLASS}
                      placeholder="Prompt Description"
                      value={input.value}
                      onChange={(e) => input.onChange(e.target.value)}
                      onBlur={input.onBlur}
                      onFocus={input.onFocus}
                    />
                    <div className="min-h-4 text-xs text-red-600">{meta.touched && meta.error ? meta.error : ''}</div>
                  </div>
                )}
              </Field>

              <Field<string> name="prompt">
                {({ input, meta }) => (
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="toolkit-prompt" className="text-theme-black block text-sm font-[600]">
                      Prompt
                    </label>
                    <InputTextarea
                      id="toolkit-prompt"
                      rows={6}
                      className="!text-theme-black !border-theme-border-grey !bg-theme-white max-h-[145px] min-h-[145px] w-full resize-none overflow-y-auto rounded-lg !text-sm placeholder:!text-sm"
                      placeholder="Prompt"
                      value={input.value}
                      onChange={(e) => input.onChange(e.target.value)}
                      onBlur={input.onBlur}
                      onFocus={input.onFocus}
                    />
                    <div className="min-h-4 text-xs text-red-600">{meta.touched && meta.error ? meta.error : ''}</div>
                  </div>
                )}
              </Field>

              <Field<string> name="comments">
                {({ input, meta }) => (
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="toolkit-comments" className="text-theme-black block text-sm font-[600]">
                      Comments
                    </label>
                    <InputTextarea
                      id="toolkit-comments"
                      rows={3}
                      className="!text-theme-black !border-theme-border-grey !bg-theme-white max-h-[80px] min-h-[80px] w-full resize-none overflow-y-auto rounded-lg !text-sm placeholder:!text-sm"
                      placeholder="Comments (e.g. if you think a new group should be added)"
                      value={input.value}
                      onChange={(e) => input.onChange(e.target.value)}
                      onBlur={input.onBlur}
                      onFocus={input.onFocus}
                    />
                    <div className="min-h-4 text-xs text-red-600">{meta.touched && meta.error ? meta.error : ''}</div>
                  </div>
                )}
              </Field>

              <div className="text-theme-text-grey mt-3 flex items-center justify-center gap-2 text-xs">
                <DefaultWarningButtonIcon className="h-[16px] w-[18px] shrink-0" aria-hidden />
                <span>Please make sure this prompt contains no PII before saving.</span>
              </div>

              <div className="flex justify-end pt-1">
                <Button
                  type="submit"
                  disabled={invalid || submitting || isLoading}
                  className="bg-theme-dark-green hover:bg-theme-dark-green text-theme-white justify-center disabled:opacity-50"
                >
                  Submit
                </Button>
              </div>
            </form>
          );
        }}
      </Form>
    </Dialog>
  );
};
